import axios from './api.js'
import saveAs from 'file-saver'
import formatContentJson from './../js/helpers/formatContentJson'

function ensureContentJson (state, project, profile) {
  let cjson = project.contentJson
  profile.phases.forEach((profilePhase, index) => {
    if (!cjson.phases.some(phase => phase.code === profilePhase.code)) {
      cjson.phases.push({
        code: profilePhase.code,
        profilePhaseId: profilePhase.profilePhaseId,
        name: profilePhase.name,
        goals: []
      })
    }
  })

  cjson.phases.forEach((phase, index) => {
    phase.index = index
    let profilePhase = profile.phases.find(profilePhase => profilePhase.code === phase.code)
    for (let i = 1; i <= 17; i++) {
      let profileGoal = profilePhase.goals.some(profileGoal => profileGoal.code === i)
      let foundGoal = phase.goals.find(goal => goal.code === i)
      if (!foundGoal) {
        phase.goals.push({
          code: i,
          selected: project.projectId ? false : profileGoal,
          tasks: []
        })
      } else {
        foundGoal.selected = true
        foundGoal.tasks.filter(task => task.custom).forEach(task => {
          task.edit = false
        })
      }
    }
  })

  cjson.phases.forEach(phase => {
    let profilePhase = profile.phases.find(profilePhase => profilePhase.code === phase.code)
    phase.goals.forEach(goal => {
      let profileGoal = profilePhase.goals.find(profileGoal => profileGoal.code === goal.code)
      if (profileGoal) {
        profileGoal.tasks.forEach(profileTask => {
          let foundTask = goal.tasks.find(task => task.code === profileTask.code)
          if (!foundTask) {
            goal.tasks.push({
              code: profileTask.code,
              custom: false,
              title: profileTask.title,
              description: profileTask.description,
              what: '',
              how: '',
              completedPct: 0,
              selected: false,
              negative: false
            })
          } else {
            foundTask.selected = true
          }
        })
        goal.tasks.sort((a, b) => {
          let codeA = a.code.padStart(3, '0')
          let codeB = b.code.padStart(3, '0')

          if (codeA === codeB) {
            return 0
          } else {
            return codeA > codeB ? 1 : -1
          }
        })
      }
    })
  })
}

function resetAllEditTasks (goals) {
  goals.forEach(goal => {
    if (goal.tasks && goal.tasks.length > 0) {
      goal.tasks.forEach(task => {
        if (task.custom && task.edit) {
          task.edit = false
        }
      })
    }
  })
}

const state = {
  // activePageCode
  activePageCode: 'landing',
  activePageViewContext: 'results',
  // modal
  showModal: false,
  modalType: null,
  modalData: null,
  // project & profile
  project: {
    contentJson: {
      name: '',
      customer: '',
      responsible: '',
      description: ''
    }
  },
  profile: null,
  profiles: [],
  // phase
  activePhaseIndex: 0,
  // icons
  iconsPath: [],
  // update result wheel
  taskDataChanged: null
}

const getters = {
  // activePageCode
  getActivePageCode: state => {
    return state.activePageCode
  },
  getActivePageViewContext: state => {
    return state.activePageViewContext
  },
  // modal
  getShowModal: state => {
    return state.showModal
  },
  getModalType: state => {
    return state.modalType
  },
  getModalData: state => {
    return state.modalData
  },
  // project & profile
  getProject: state => {
    return state.project
  },
  getProfile: state => {
    return state.profile
  },
  getProfiles: state => {
    return state.profiles
  },
  getProfileGoals: state => {
    return state.profile.goals
  },
  getProfilePhaseIndex: state => (phaseCode) => {
    return state.profile.phases.findIndex(ph => ph.code === phaseCode)
  },
  getProfileGoal: state => (goalCode) => {
    return state.profile.goals.find(goal => goal.code === goalCode)
  },
  getActivePhaseSelectedGoals: (state, getters) => {
    let goals = getters.getPhaseGoals()
    return goals.filter(goal => goal.selected).sort((a, b) => a.code - b.code)
  },
  getPhaseGoals: state => (phaseIndex, onlySelected) => {
    if (phaseIndex === undefined) {
      phaseIndex = state.activePhaseIndex
    }
    let selectedPhase = state.project.contentJson.phases.find(phase => phase.code === state.profile.phases[phaseIndex].code)
    if (selectedPhase) {
      if (onlySelected) {
        return selectedPhase.goals.filter(goal => goal.selected).sort((a, b) => a.code - b.code)
      }
      return selectedPhase.goals.sort((a, b) => a.code - b.code)
    }
  },
  // phase
  getActivePhase: state => {
    return state.project.contentJson.phases[state.activePhaseIndex]
  },
  getActivePhaseIndex: state => {
    return state.activePhaseIndex
  },
  getActiveProfilePhase: state => {
    return state.profile.phases[state.activePhaseIndex]
  },
  getProjectPhases: state => {
    return state.project.contentJson.phases
  },
  // icons
  getIconsPath: state => {
    return state.iconsPath
  },
  // result wheel
  getTaskDataChanged: state => {
    return state.taskDataChanged
  }
}

const mutations = {
  // activePageCode
  setActivePageCode: (state, payload) => {
    state.activePageCode = payload
  },
  setActivePageViewContext: (state, payload) => {
    state.activePageViewContext = payload
  },
  // modal
  setShowModal: (state, payload) => {
    state.showModal = payload
  },
  setModalType: (state, payload) => {
    if (!payload) {
      setTimeout(() => {
        state.modalType = payload
      }, 200)
    } else {
      state.modalType = payload
    }
  },
  setModalData: (state, payload) => {
    state.modalData = payload
  },
  setProject: (state, payload) => {
    ensureContentJson(state, payload, state.profile)
    state.project = payload
  },
  updateProject: (state, payload) => {
    state.project.contentJson.name = payload.name
    state.project.contentJson.customer = payload.customer
    state.project.contentJson.responsible = payload.responsible
    state.project.contentJson.description = payload.description
  },
  setProfile: (state, payload) => {
    state.profile = payload
  },
  setProfiles: (state, payload) => {
    state.profiles = payload
  },
  setSelectedGoal: (state, payload) => {
    let phase = state.project.contentJson.phases[state.activePhaseIndex]
    let foundGoal = phase.goals.find(goal => goal.code === payload)
    foundGoal.selected = !foundGoal.selected
  },
  // phase
  setActivePhaseIndex: (state, payload) => {
    state.activePhaseIndex = payload - 1
  },
  // icons
  setIconsPath: (state) => {
    let paths = []

    for (let i = 1; i <= 17; i++) {
      paths.push('/img/icons/sdg/da/SDG-' + i + '.svg')
    }

    state.iconsPath = paths
  },
  setTaskDataChanged: state => {
    state.taskDataChanged = new Date()
  }
}

const actions = {
  // activePageCode
  setActivePageCode: ({ commit }, page) => {
    commit('setActivePageCode', page)
  },
  setActivePageViewContext: ({ commit }, context) => {
    commit('setActivePageViewContext', context)
  },
  // modal
  setShowModal: ({ commit }, boolean) => {
    commit('setShowModal', boolean)
  },
  setModalType: ({ commit }, modalType) => {
    commit('setModalType', modalType)
  },
  setModalData: ({ commit }, modalData) => {
    commit('setModalData', modalData)
  },
  // project & profile
  async setProject ({ commit }, projectId) {
    try {
      const response = await axios.get('/Public/GetProject?ProjectId=' + projectId)
      if (response) {
        commit('setProject', response.data)
      }
    } catch (err) {
      throw err
    }
  },
  setNewProject: ({ commit }, project) => {
    commit('setProject', project)
  },
  updateProject: ({ commit }, project) => {
    commit('updateProject', project)
  },
  async setProfile ({ commit }, profileId) {
    try {
      const response = await axios.get('/Public/GetProfile?ProfileId=' + profileId)
      if (response) {
        commit('setProfile', response.data)
      }
    } catch (err) {
      throw err
    }
  },
  async setProfiles ({ commit }) {
    try {
      const response = await axios.get('/Public/GetProfileList')
      if (response) {
        commit('setProfiles', response.data)
      }
    } catch (err) {
      throw err
    }
  },
  setSelectedGoal: ({ commit }, goal) => {
    commit('setSelectedGoal', goal)
  },
  // phase
  setActivePhaseIndex: ({ commit, dispatch }, phase) => {
    dispatch('resetAllEditTasksAction')
    commit('setActivePhaseIndex', phase)
  },
  // icons
  setIconsPath: ({ commit }) => {
    commit('setIconsPath')
  },
  async handleSaveProject ({ commit }, operator) {
    let saveProject = formatContentJson(state.project.contentJson)

    let dataObject = {
      contentJson: JSON.stringify(saveProject),
      profileId: state.project.profileId,
      createdBy: state.project.createdBy ? state.project.createdBy : null
    }

    dataObject.projectId = state.project.projectId ? state.project.projectId : null
    dataObject.overwrite = operator === 'overwrite'

    try {
      const response = await axios.post('/Public/SaveProject', dataObject)
      if (response) {
        state.project.projectId = response.data.projectId
        state.project.createdDate = response.data.createdDate
        state.project.date = response.data.date
      }
    } catch (err) {
      throw err
    }
  },
  setTaskDataChanged: ({ commit }) => {
    commit('setTaskDataChanged')
  },
  async setPDF ({ commit, rootState }, pdfObject) {
    let contentJson = formatContentJson(state.project.contentJson, rootState.result.activePhaseIndexes)
    pdfObject.projectJson = JSON.stringify(contentJson)

    try {
      const response = await axios.post('/Public/CreateLatestPdf', pdfObject)
      if (response) {
        try {
          const responseStream = await axios.getPDF('/Public/GetPdfStream?id=' + response.data.id + '&FileName=' + state.project.contentJson.name)
          if (responseStream) {
            let blob = new Blob([responseStream.data])
            saveAs(blob, state.project.contentJson.name + '.pdf')
          }
        } catch (err) {
          throw err
        }
      }
    } catch (err) {
      throw err
    }
  },
  async setHistoryPDF ({ commit, rootState }, pdfObject) {
    try {
      const response = await axios.post('/Public/CreateHistoryPdf', pdfObject)
      if (response) {
        try {
          const responseStream = await axios.getPDF('/Public/GetPdfStream?id=' + response.data.id + '&FileName=' + state.project.contentJson.name)
          if (responseStream) {
            let blob = new Blob([responseStream.data])
            saveAs(blob, state.project.contentJson.name + '.pdf')
          }
        } catch (err) {
          throw err
        }
      }
    } catch (err) {
      throw err
    }
  },
  resetAllEditTasksAction: ({ commit }) => {
    resetAllEditTasks(state.project.contentJson.phases[state.activePhaseIndex].goals.filter(goal => goal.selected))
  },
  resetAllInVuex: ({ commit, dispatch, rootState }) => {
    state.profile = null
    state.profiles = []
    state.activePhaseIndex = 0

    rootState.task.activeGoalCode = null
    rootState.result.activePhaseIndexes = []
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}
